<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';

import Button from 'dashboard/components-next/button/Button.vue';
import BarChart from 'shared/components/charts/BarChart.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const metrics = ref(null);
const isLoading = ref(false);
const hasError = ref(false);

const baseKey = 'CAMPAIGN.WHATSAPP.METRICS';

const fetchMetrics = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    metrics.value = await store.dispatch('campaigns/getMetrics', props.campaign.id);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// Realtime: atualiza só os totais (a série recarrega no próximo fetch manual).
const onMetricsUpdated = payload => {
  if (payload?.campaign_id !== props.campaign.id || !metrics.value) return;
  metrics.value = { ...metrics.value, totals: payload.totals };
};

onMounted(() => {
  fetchMetrics();
  emitter.on('campaign.metrics.updated', onMetricsUpdated);
});

onBeforeUnmount(() => {
  emitter.off('campaign.metrics.updated', onMetricsUpdated);
});

const totals = computed(() => metrics.value?.totals ?? {});
const rates = computed(() => metrics.value?.rates ?? {});

const totalCards = computed(() => [
  { key: 'TOTAL_CONTACTS', value: totals.value.total_contacts ?? 0 },
  { key: 'SENT', value: totals.value.sent_count ?? 0 },
  { key: 'DELIVERED', value: totals.value.delivered_count ?? 0 },
  { key: 'READ', value: totals.value.read_count ?? 0 },
  { key: 'FAILED', value: totals.value.failed_count ?? 0 },
  { key: 'REPLIES', value: totals.value.reply_count ?? 0 },
]);

const rateRows = computed(() => [
  { key: 'DELIVERED', value: rates.value.delivered ?? 0 },
  { key: 'READ', value: rates.value.read ?? 0 },
  { key: 'FAILED', value: rates.value.failed ?? 0 },
  { key: 'REPLY', value: rates.value.reply ?? 0 },
]);

const chartCollection = computed(() => {
  const series = metrics.value?.time_series ?? [];
  return {
    labels: series.map(point => point.date),
    datasets: [
      {
        label: t(`${baseKey}.SERIES.SENT`),
        backgroundColor: '#1f93ff',
        data: series.map(point => point.sent),
      },
      {
        label: t(`${baseKey}.SERIES.DELIVERED`),
        backgroundColor: '#44ce4b',
        data: series.map(point => point.delivered),
      },
      {
        label: t(`${baseKey}.SERIES.FAILED`),
        backgroundColor: '#ff382d',
        data: series.map(point => point.failed),
      },
    ],
  };
});

const hasSeries = computed(() => (metrics.value?.time_series ?? []).length > 0);
</script>

<template>
  <section class="flex flex-col gap-4">
    <header class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t(`${baseKey}.TITLE`) }}
      </h3>
      <Button
        variant="faded"
        color="slate"
        size="sm"
        type="button"
        :label="t(`${baseKey}.REFRESH`)"
        :is-loading="isLoading"
        @click="fetchMetrics"
      />
    </header>

    <p v-if="isLoading && !metrics" class="text-sm text-n-slate-11">
      {{ t(`${baseKey}.LOADING`) }}
    </p>

    <p v-else-if="hasError" class="text-sm text-n-ruby-11">
      {{ t(`${baseKey}.ERROR`) }}
    </p>

    <p v-else-if="!metrics" class="text-sm text-n-slate-11">
      {{ t(`${baseKey}.EMPTY`) }}
    </p>

    <template v-else>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div
          v-for="card in totalCards"
          :key="card.key"
          class="flex flex-col gap-1 rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
        >
          <span class="text-xs text-n-slate-11">
            {{ t(`${baseKey}.TOTALS.${card.key}`) }}
          </span>
          <span class="text-xl font-semibold text-n-slate-12">
            {{ card.value }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t(`${baseKey}.RATES.TITLE`) }}
        </h4>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div
            v-for="row in rateRows"
            :key="row.key"
            class="flex flex-col gap-1 rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
          >
            <span class="text-xs text-n-slate-11">
              {{ t(`${baseKey}.RATES.${row.key}`) }}
            </span>
            <span class="text-lg font-semibold text-n-slate-12">
              {{ row.value }}%
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t(`${baseKey}.SERIES.TITLE`) }}
        </h4>
        <div v-if="hasSeries" class="h-64 w-full">
          <BarChart :collection="chartCollection" />
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ t(`${baseKey}.EMPTY`) }}
        </p>
      </div>
    </template>
  </section>
</template>
